import type { FragmentType } from 'src/gql';
import type { Deprecated_HandleItemUpdate } from 'src/types/account';
import { useState } from 'react';
import ItemDialogBackButton from 'src/components/mui/item-dialog-back-button';
import ItemDialogYesNoButtons from 'src/components/mui/item-dialog-yes-no-buttons';
import ItemHeader from 'src/components/portfolio/views/item-header';
import TrackedButton from 'src/components/tracked/tracked-button';
import { UpdateType } from 'src/constants/portfolio';
import { graphql, unmaskFragment } from 'src/gql';

import type { ID } from '@savvy/utils/id';
import { ASSET_CLASS_REAL_ESTATE } from '@savvy/constants/asset-classes';

import LiabilityForm from '../liability/liability-form';
import AssetForm from './asset-form';

export const AddEditRealEstateFragment = graphql(/* GraphQL */ `
  fragment AddEditRealEstate on Asset {
    id
    ...AssetForm
    ...LiabilityFormAsset
    liabilities {
      id
      ...LiabilityForm
    }
  }
`);

const enum FlowStep {
  RealEstateBasics = 'RealEstateBasics',
  MortgageDetails = 'MortgageDetails',
}

const enum MortageState {
  UNKNOWN = 'undefined',
  YES = 'true',
  NO = 'false',
}

// Real estate is represented as an asset and and optionally attached liability
export default function AddEditRealEstate({
  assetFrag,
  clientUserId,
  hiddenFromClient,
  onClickBack,
  onSuccess,
}: {
  readonly assetFrag?: FragmentType<typeof AddEditRealEstateFragment>;
  readonly clientUserId?: ID;
  readonly hiddenFromClient?: boolean;
  readonly onClickBack?: () => void;
  readonly onSuccess: Deprecated_HandleItemUpdate;
}) {
  const asset = unmaskFragment(AddEditRealEstateFragment, assetFrag);
  let isEditMode = !!asset;
  const [flowStep, setFlowStep] = useState(FlowStep.RealEstateBasics);
  const [mortgageState, setMortgageState] = useState(
    asset?.liabilities && asset.liabilities.length > 0 ? MortageState.YES : MortageState.UNKNOWN,
  );
  const [assetId, setAssetId] = useState<number | null>(null);

  if (flowStep === FlowStep.RealEstateBasics) {
    return (
      <>
        {onClickBack && (
          <ItemDialogBackButton
            trackedElementName={isEditMode ? 'update-real-estate-back-button' : 'create-real-estate-back-button'}
            onClick={onClickBack}
          />
        )}
        <ItemHeader text="Real estate details" />
        <AssetForm
          assetFrag={asset}
          clientUserId={clientUserId}
          hiddenFromClient={hiddenFromClient}
          presetAssetClass={ASSET_CLASS_REAL_ESTATE}
          onSuccess={() => {
            setFlowStep(FlowStep.MortgageDetails);
            return Promise.resolve();
          }}
          setAssetId={setAssetId}
        />
      </>
    );
  }
  return (
    <>
      <ItemDialogBackButton
        trackedElementName={isEditMode ? 'update-real-estate-back-button' : 'create-real-estate-back-button'}
        onClick={() => {
          setFlowStep(FlowStep.RealEstateBasics);
          // eslint-disable-next-line react-compiler/react-compiler -- FIXME: Holy - we should be using state correctly. What is happening here??
          isEditMode = true;
        }}
      />
      <ItemHeader text="Do you have a mortgage for this real estate?" />
      <div className="flex flex-row gap-0">
        <div className="mb-6">
          <ItemDialogYesNoButtons
            yesButtonElementName={isEditMode ? 'update-real-estate-yes-button' : 'create-real-estate-yes-button'}
            noButtonElementName={isEditMode ? 'update-real-estate-no-button' : 'create-real-estate-no-button'}
            onClick={(value) => {
              const newMortgageState = value ? MortageState.YES : MortageState.NO;
              setMortgageState(newMortgageState);
            }}
          />
        </div>
      </div>
      {mortgageState === MortageState.YES && (
        <LiabilityForm
          assetFrag={asset}
          clientUserId={clientUserId}
          isEditMode={isEditMode}
          hiddenFromClient={hiddenFromClient}
          liabilityFrag={asset?.liabilities[0]}
          onSuccess={onSuccess}
          assetId={assetId}
          isMortgage
        />
      )}
      {mortgageState === MortageState.NO && (
        <div className="flex flex-row justify-end mt-4 mb-6">
          <TrackedButton
            elementName={isEditMode ? 'update-real-estate-save-button' : 'create-real-estate-save-button'}
            onClick={() => {
              onSuccess({
                // In the add flow `asset` is null until creation; AssetForm captures the new id into `assetId`.
                id: assetId ?? asset?.id ?? '',
                type: isEditMode ? UpdateType.UPDATE_REAL_ESTATE : UpdateType.CREATE_REAL_ESTATE,
              });
            }}
          >
            Save
          </TrackedButton>
        </div>
      )}
    </>
  );
}
